/** Structural host seam from W1-push SPEC §2.6. Push owns its implementation under core/push.
 * Its factory binds here so hooks and the shell share one controller; there is no permission or provider code here.
 */
import { LiveValue, constantLive, type HyloServices, type Id, type Live, type SessionLifetime } from '@/model';
import type { QueryClient } from '@tanstack/react-query';

export type PushFailure = 'permission' | 'registration' | 'network' | 'unavailable' | 'action';
export interface WebPushState {
  availability: 'loading' | 'available' | 'unavailable' | 'unsupported' | 'installRequired';
  permission: 'default' | 'denied' | 'granted';
  registration: 'idle' | 'subscribing' | 'registered' | 'disabled' | 'error';
  busy: boolean; showsOffNotice: boolean; firstSendPrompt: boolean; failure: PushFailure | null;
}
export interface PushIdentity { accountId: Id; deviceId: Id; realmId: Id | null }
export type PushIntent = { id: Id; identity: PushIdentity; generation: Id; createdAt: number; expiresAt: number } & (
  | { kind: 'openChat'; conversationId: Id; messageId?: Id; seq?: number }
  | { kind: 'reply' | 'markRead'; conversationId: Id; messageId: Id; seq: number }
  | { kind: 'openRequests' | 'accept' | 'decline'; requestId: Id }
  | { kind: 'openItem'; itemId: Id | null; proposalId?: Id }
  | { kind: 'openHome' }
);
export interface WebPushHost {
  registration: Promise<ServiceWorkerRegistration>; queryClient: QueryClient;
  language(): 'en' | 'zh-Hans'; onPermissionNudge(): void;
  onNavigate(intent: PushIntent): Promise<void>; onFeedback(code: PushFailure): void;
}
export interface WebPushController {
  readonly state: Live<WebPushState>;
  start(): Promise<void>; enableFromGesture(): Promise<void>; disable(): Promise<void>; retryRegistration(): Promise<void>;
  dismissNotice(): void; dismissFirstSendPrompt(): void; noteMessageQueued(): void;
  setForegroundConversation(id: Id | null): void; reconcile(): Promise<void>; dispose(): void;
}
export const unavailablePushState = constantLive<WebPushState>(Object.freeze({ availability: 'unavailable', permission: 'default', registration: 'idle', busy: false, showsOffNotice: false, firstSendPrompt: false, failure: null }));
interface Entry { live: LiveValue<WebPushController | undefined>; lifetime: SessionLifetime | null }
const controllers = new WeakMap<HyloServices, Entry>();
export function webPushControllerLive(services: HyloServices): Live<WebPushController | undefined> {
  let entry = controllers.get(services);
  if (!entry) {
    entry = { live: new LiveValue<WebPushController | undefined>(undefined), lifetime: null }; controllers.set(services, entry);
    const captured = entry;
    services.session.lifetime.subscribe(() => { if (captured.lifetime !== services.session.lifetime.get()) { captured.lifetime = null; captured.live.set(undefined); } });
  }
  return entry.live;
}
export function getWebPushController(services: HyloServices): WebPushController | undefined { return webPushControllerLive(services).get(); }
/** Called by Push's createWebPushController; its dispose calls the returned unbind. A stale dispose cannot unbind a successor. */
export function bindWebPushController(services: HyloServices, controller: WebPushController): () => void {
  webPushControllerLive(services); const entry = controllers.get(services)!;
  const lifetime = services.session.lifetime.get();
  if (!lifetime) return () => undefined;
  entry.lifetime = lifetime; entry.live.set(controller);
  return () => { if (entry.live.get() === controller) { entry.live.set(undefined); entry.lifetime = null; } };
}
